<template>
  <div class="recaptcha-checkbox" :class="{ 'is-error': hasError }">
    <div class="recaptcha-checkbox__header">
      <span class="recaptcha-checkbox__shield" aria-hidden="true">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.8"
            d="M12 3 5.5 5.6v5.2c0 4.4 2.7 8.3 6.5 10.2 3.8-1.9 6.5-5.8 6.5-10.2V5.6L12 3Z"
          />
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.8"
            d="m9.4 12 1.7 1.7 3.7-4"
          />
        </svg>
      </span>
      <div>
        <p class="recaptcha-checkbox__title">Verificação de segurança</p>
        <p class="recaptcha-checkbox__description">
          Confirme que você não é um robô para continuar.
        </p>
      </div>
    </div>

    <div v-if="!hasError" class="recaptcha-checkbox__widget-shell">
      <div ref="container" class="recaptcha-checkbox__widget"></div>
      <div
        v-if="widgetId === null"
        class="recaptcha-checkbox__loading"
        role="status"
        aria-live="polite"
      >
        <span class="recaptcha-checkbox__spinner" aria-hidden="true"></span>
        <span>Preparando verificação segura...</span>
      </div>
    </div>

    <div v-if="hasError" class="recaptcha-checkbox__error" role="alert">
      <svg
        class="recaptcha-checkbox__error-icon"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 9v4m0 4h.01M10.3 4.9 2.9 18a1.4 1.4 0 0 0 1.2 2.1h15.8a1.4 1.4 0 0 0 1.2-2.1L13.7 4.9a2 2 0 0 0-3.4 0Z"
        />
      </svg>
      <div>
        <p class="recaptcha-checkbox__error-message">{{ statusMessage }}</p>
        <button
          type="button"
          class="recaptcha-checkbox__retry"
          @click="retry"
        >
          Tentar novamente
        </button>
      </div>
    </div>

    <p
      v-else-if="statusMessage && widgetId !== null"
      class="recaptcha-checkbox__status"
      role="status"
      aria-live="polite"
    >
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="m5 12 4 4L19 6"
        />
      </svg>
      {{ statusMessage }}
    </p>
  </div>
</template>

<script>
import { loadRecaptchaScript } from "@/services/recaptcha";
import {
  createRecaptchaWidgetOptions,
  handleRecaptchaError,
  retryRecaptcha,
} from "@/components/recaptchaController";

export default {
  name: "RecaptchaCheckbox",
  props: {
    siteKey: {
      type: String,
      default: () => process.env.VUE_APP_RECAPTCHA_SITE_KEY || "",
    },
  },
  emits: ["verified", "expired", "error"],
  data() {
    return {
      recaptchaApi: null,
      widgetId: null,
      statusMessage: "Carregando verificação de segurança...",
      hasError: false,
      isUnmounted: false,
    };
  },
  mounted() {
    this.initialize();
  },
  beforeUnmount() {
    this.isUnmounted = true;
    this.resetWidget();
  },
  methods: {
    async initialize() {
      if (!this.siteKey) {
        this.handleError(
          "Verificação de segurança indisponível. Contate o suporte.",
        );
        return;
      }

      try {
        this.recaptchaApi = await loadRecaptchaScript();
        if (this.isUnmounted) return;

        this.widgetId = this.recaptchaApi.render(
          this.$refs.container,
          createRecaptchaWidgetOptions({
            siteKey: this.siteKey,
            onVerified: this.handleVerified,
            onExpired: this.handleExpired,
            onError: this.handleError,
          }),
        );
        this.statusMessage = "";
      } catch (error) {
        this.handleError();
      }
    },
    handleVerified(token) {
      this.hasError = false;
      this.statusMessage = "Verificação concluída.";
      this.$emit("verified", token);
    },
    handleExpired() {
      this.hasError = false;
      this.statusMessage = "A verificação expirou. Confirme novamente.";
      this.$emit("expired");
      this.resetWidget();
    },
    handleError(
      message = "Não foi possível concluir a verificação. Tente novamente.",
    ) {
      handleRecaptchaError(this, message);
    },
    resetWidget() {
      if (this.recaptchaApi && this.widgetId !== null) {
        this.recaptchaApi.reset(this.widgetId);
      }
    },
    reset() {
      this.hasError = false;
      this.statusMessage = "Confirme novamente a verificação de segurança.";
      this.resetWidget();
    },
    retry() {
      return retryRecaptcha(this);
    },
  },
};
</script>

<style scoped>
.recaptcha-checkbox {
  width: 100%;
  padding: 16px;
  overflow: hidden;
  color: #ffffff;
  background: rgba(15, 23, 42, 0.48);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 14px;
  transition:
    border-color 180ms ease,
    background-color 180ms ease;
}

.recaptcha-checkbox.is-error {
  background: rgba(69, 10, 10, 0.28);
  border-color: rgba(252, 165, 165, 0.38);
}

.recaptcha-checkbox__header {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
}

.recaptcha-checkbox__shield {
  display: grid;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  color: #bfdbfe;
  background: rgba(59, 130, 246, 0.18);
  border-radius: 10px;
  place-items: center;
}

.recaptcha-checkbox__shield svg {
  width: 21px;
  height: 21px;
}

.recaptcha-checkbox__title {
  margin: 0;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
}

.recaptcha-checkbox__description {
  margin: 2px 0 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.75rem;
  line-height: 1.125rem;
}

.recaptcha-checkbox__widget-shell {
  position: relative;
  display: flex;
  min-height: 78px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 4px;
}

.recaptcha-checkbox__widget {
  min-height: 78px;
  width: 304px;
  max-width: 100%;
}

.recaptcha-checkbox__loading {
  position: absolute;
  inset: 0;
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.76);
  background: rgba(255, 255, 255, 0.06);
  font-size: 0.75rem;
}

.recaptcha-checkbox__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.24);
  border-top-color: #bfdbfe;
  border-radius: 50%;
  animation: recaptcha-spin 720ms linear infinite;
}

.recaptcha-checkbox__status {
  display: flex;
  gap: 6px;
  align-items: center;
  margin: 10px 0 0;
  color: #bbf7d0;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.125rem;
}

.recaptcha-checkbox__status svg {
  width: 15px;
  height: 15px;
  flex: 0 0 15px;
}

.recaptcha-checkbox__error {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  color: #fecaca;
}

.recaptcha-checkbox__error-icon {
  width: 19px;
  height: 19px;
  flex: 0 0 19px;
  margin-top: 1px;
}

.recaptcha-checkbox__error-message {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.125rem;
}

.recaptcha-checkbox__retry {
  margin-top: 7px;
  min-height: 28px;
  background: transparent;
  border: 0;
  color: #ffffff;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.recaptcha-checkbox__retry:hover {
  color: #dbeafe;
}

.recaptcha-checkbox__retry:focus-visible {
  outline: 2px solid #bfdbfe;
  outline-offset: 4px;
  border-radius: 2px;
}

@keyframes recaptcha-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 360px) {
  .recaptcha-checkbox {
    padding: 14px;
  }

  .recaptcha-checkbox__widget-shell {
    min-height: 70px;
    justify-content: flex-start;
  }

  .recaptcha-checkbox__widget {
    width: 304px;
    min-height: 78px;
    transform: scale(0.88);
    transform-origin: left top;
  }
}

@media (prefers-reduced-motion: reduce) {
  .recaptcha-checkbox,
  .recaptcha-checkbox__spinner {
    transition: none;
    animation: none;
  }
}
</style>
